import { FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../components/CommonComps/Header';
import ChangePasswordModal from '../components/CommonComps/Modals/ChangePasswordModal';
import MobileSideNavbar from '../components/CommonComps/Navbars/MobileSideNavbar';
import SideNavbar from '../components/CommonComps/Navbars/SideNavbar';
import { useAuthContext } from '../contexts/AuthProvider';
import { useNavbarContext } from '../contexts/NavbarProvider';
import useAssociateActions from '../hooks/APIHooks/users/useAssociateActions';
import useAuthActions from '../hooks/APIHooks/users/useAuthActions';
import { AssociatePasswordChangeBody } from '../types/associates';
import { deleteToken } from '../utils/cookieHandler';
import { fetchErrorMessage } from '../utils/fetchErrorMessage';

const GlobalLayout = () => {
  // integration of react hooks here
  const [showChangeModal, setShowChangeModal] = useState<boolean>(false);

  // integration of custom hooks here
  const { showNavbar, isMobile } = useNavbarContext();
  const { currentUser, isFirstLogin, setCurrentUserId, currentUserType } =
    useAuthContext();

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // integration of API hooks here
  const { changeAssociatePasswordHandler } = useAssociateActions('');
  const { userLogoutHandler } = useAuthActions();

  // triggering change password modal if user logged in for the first time here
  useEffect(() => {
    if (isFirstLogin && currentUserType === 'user') {
      setShowChangeModal(true);
    }
  }, [isFirstLogin, currentUser, currentUserType]);

  // function to navigate to top on load declared here
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // handler function to handle change password for 1st time login declared here
  const changePasswordHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: AssociatePasswordChangeBody = {
        current_password: values.current_password,
        password: values.new_password,
        is_first_login: false,
        updated_by: currentUser?.username as string,
      };

      await changeAssociatePasswordHandler
        .mutateAsync({ associateId: currentUser?.id as number, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Password Changed!! Please, Login Again..');

            userLogoutHandler.mutateAsync().then((res) => {
              if (res.is_success) {
                deleteToken();

                setCurrentUserId(0);

                sessionStorage.clear();
                localStorage.clear();

                navigate('/');
              }
            });
          } else {
            toast.error(res.message || 'Password Change Failed!!');
          }
        })
        .catch((error) => {
          toast.error(fetchErrorMessage(error));
        });
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering global layout component here
  return (
    <>
      <Header />

      <main className='font-lato'>
        {isMobile ? <MobileSideNavbar /> : <SideNavbar />}
        <motion.section
          initial={
            showNavbar
              ? isMobile
                ? { paddingLeft: 0 }
                : { paddingLeft: '260px' }
              : isMobile
              ? { paddingLeft: 0 }
              : { paddingLeft: '70px' }
          }
          animate={
            showNavbar
              ? isMobile
                ? { paddingLeft: 0 }
                : { paddingLeft: '260px' }
              : isMobile
              ? { paddingLeft: 0 }
              : { paddingLeft: '70px' }
          }
          className='pt-28 w-[95%] md:w-[96%] mx-auto mb-10 space-y-10'
        >
          <Outlet />
        </motion.section>
      </main>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showChangeModal ? (
          <ChangePasswordModal
            handleModalClose={() => {}}
            changePasswordFn={changePasswordHandler}
            isRequestedFromUser
            isFirstLogin
            hideCloseButton
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default GlobalLayout;
